import type { ComponentDocs } from '../component-docs.js';

/**
 * What `<caos-result-grid>` says about itself.
 *
 * The question this component answers, and the reason it is not `<caos-data-table>` with a footer
 * bolted on, is WHO OWNS THE ORDER.
 *
 * A list surface holds one page of a set that lives on the server, so the only correct answer to a
 * header press is to fetch a differently-ordered page — which is why the table raises `caos-sort`
 * and sorts nothing. A query result is the opposite: the whole set is already here, so ordering it
 * is a local fact, and asking the application to re-run somebody's query to change a column order
 * would be both slow and wrong, because re-running can return different rows.
 *
 * So this component sorts and pages the rows it was handed, and composes the table for the drawing.
 * One table, two genuinely different answers about who owns the order.
 *
 * Empties sort LAST in both directions. A null is the absence of a value rather than a small one,
 * and floating nulls to the top of a descending sort buries the rows somebody asked to see.
 */
const docs: ComponentDocs = {
  aliases: ['query results', 'result table', 'result set', 'query output grid', 'records table'],

  answers: [
    'show what a query returned',
    'sort a result set without re-running the query',
    'page through a large result in the browser',
    'let somebody open a record from a result row',
    'export a result to a file',
  ],

  placements: ['appPage'],

  whenToUse:
    'When the WHOLE set of rows is already in hand — a query result, a comparison, an import ' +
    'preview — and sorting or paging it should not involve going back to the server.',

  insteadUse: [
    {
      name: 'DataTable',
      when:
        'the rows are one server-held page of a larger set. The table raises a sort request and ' +
        'orders nothing, which is the right behaviour there and the wrong one here.',
    },
    {
      name: 'TestResultTable',
      when: 'the rows are test results — that component knows what a failure and a stack trace are.',
    },
  ],

  inputs: [
    {
      name: 'columns',
      type: 'json',
      description:
        'The columns: `{ key, label, sortable?, align?, width? }[]` — the table’s own column shape, ' +
        'so nothing is transliterated. Columns are sortable unless a column says `sortable: false`, ' +
        'which is the opposite default from the table: everything in a result set in hand can be ' +
        'ordered, and requiring a flag per column would mean most of them silently could not be.',
      default: [],
    },
    {
      name: 'rows',
      type: 'json',
      description:
        'The whole result set, each row a map of column key to value. Setting it clears the sort and ' +
        'returns to page 1 — staying on page 7 of the previous result would show an empty grid and ' +
        'let somebody conclude their query returned nothing.',
      default: [],
    },
    {
      name: 'page',
      attribute: 'page',
      type: 'number',
      property: true,
      description: 'Which page is on screen, 1-based. Clamped into the pages that exist.',
      default: 1,
    },
    {
      name: 'pageSize',
      attribute: 'page-size',
      type: 'number',
      description: 'How many rows to a page.',
      default: 50,
    },
    {
      name: 'idColumns',
      attribute: 'id-columns',
      type: 'text',
      description:
        'A comma-separated list of column keys holding record ids. Cells in those columns become real ' +
        'buttons raising `caos-result-open-record`. The component never builds a URL, which is what ' +
        'keeps it usable by an application whose records live somewhere it has not heard of.',
    },
    {
      name: 'tookMs',
      attribute: 'took-ms',
      type: 'number',
      description: 'How long the query took, shown beside the row count.',
    },
    {
      name: 'truncated',
      attribute: 'truncated',
      type: 'boolean',
      description:
        'More rows matched than were returned. The summary says so, because "2,000 rows" over a ' +
        'capped set tells somebody their query matched exactly two thousand things.',
      default: false,
    },
    {
      name: 'exportLabel',
      attribute: 'export-label',
      type: 'text',
      description: 'What the export button is called.',
      default: 'Export',
    },
  ],

  events: [
    {
      name: 'caos-result-sort',
      when: 'A column header is pressed. The rows are ALREADY reordered when this fires.',
      carries: '`{ column, direction }` — for a host that wants to remember the preference, not act on it.',
    },
    {
      name: 'caos-result-page',
      when: 'The page changes through Previous or Next.',
      carries: '`{ page, pageCount }`.',
    },
    {
      name: 'caos-result-export',
      when: 'The export button is pressed.',
      carries:
        '`{ columns, rows }` — the WHOLE set in the order shown, not the page. Writing the file is ' +
        'the application’s, because a component cannot know whether this page may start a download.',
    },
    {
      name: 'caos-result-open-record',
      when: 'A record id cell is pressed.',
      carries: '`{ id, row }`.',
    },
  ],

  state: [
    {
      name: 'ordered',
      type: 'row[]',
      description: 'The whole set in the order being shown — every page, not the one on screen.',
    },
    { name: 'visibleRows', type: 'row[]', description: 'Just the rows on the current page.' },
    { name: 'pageCount', type: 'number', description: 'How many pages the set makes. An empty result is one page, never zero.' },
  ],

  parts: [
    { part: 'grid', is: 'The outer column.' },
    { part: 'summary', is: 'The row count and timing line.' },
    { part: 'table', is: 'The composed data table.' },
    { part: 'footer', is: 'The pager and export row.' },
    { part: 'pager', is: 'The Previous / Next cluster.' },
    { part: 'page-status', is: 'The "Page 2 of 7" text.' },
    { part: 'export', is: 'The export button.' },
  ],

  accessibility:
    'The summary and the page position are both `role="status"` live regions: running a query and ' +
    'turning a page are the two moments when everything on screen changes, and neither moves focus, ' +
    'so neither would otherwise be announced. Record ids are `<button>` elements with an accessible ' +
    'name reading "Open record <id>" — a bare id read aloud says nothing about being able to press ' +
    'it. Sorting is announced by the composed table’s own `aria-sort`. An empty cell is drawn empty ' +
    'rather than as the word "null", which is the shape of the transport rather than the state of ' +
    'the data.',

  examples: [
    {
      key: 'result',
      label: 'A query result',
      summary:
        'Rows in hand: press a header and they reorder here, with no request and no risk of a ' +
        're-run returning something different.',
      placement: {
        component: 'caos-result-grid',
        inputs: {
          'id-columns': 'id',
          'took-ms': 310,
          'page-size': 3,
          columns: [
            { key: 'id', label: 'Id' },
            { key: 'name', label: 'Name' },
            { key: 'industry', label: 'Industry' },
            { key: 'employees', label: 'Employees', align: 'end' },
          ],
          rows: [
            { id: '001x00000A1b2C', name: 'Northwind Traders', industry: 'Retail', employees: 420 },
            { id: '001x00000A1b2D', name: 'Contoso Marine', industry: 'Shipping', employees: 1180 },
            { id: '001x00000A1b2E', name: 'Fabrikam Metals', industry: null, employees: 95 },
            { id: '001x00000A1b2F', name: 'Tailspin Air', industry: 'Aviation', employees: 3300 },
          ],
        },
      },
      stage: { needsWidth: 720 },
    },
    {
      key: 'truncated',
      label: 'A capped result',
      summary:
        'More matched than came back. The summary says so, rather than reporting the cap as if it ' +
        'were the answer.',
      placement: {
        component: 'caos-result-grid',
        inputs: {
          truncated: true,
          'took-ms': 2140,
          columns: [
            { key: 'id', label: 'Id' },
            { key: 'name', label: 'Name' },
          ],
          rows: [
            { id: '001x00000A1b2C', name: 'Northwind Traders' },
            { id: '001x00000A1b2D', name: 'Contoso Marine' },
          ],
        },
      },
      stage: { needsWidth: 720 },
    },
    {
      key: 'empty',
      label: 'A query that matched nothing',
      summary:
        'Zero rows is a result, not an error. The grid says so, the export button is disabled ' +
        'because there is nothing to export, and the pager reads page 1 of 1.',
      placement: {
        component: 'caos-result-grid',
        inputs: {
          'took-ms': 88,
          columns: [
            { key: 'id', label: 'Id' },
            { key: 'name', label: 'Name' },
          ],
          rows: [],
        },
      },
      stage: { needsWidth: 720 },
    },
  ],
};

export default docs;
